/**
 * The bar of filters, search and actions that sits above a table.
 *
 * ## It is not `role="toolbar"`
 *
 * That role is a promise: a screen reader announces a toolbar and the person
 * then expects the arrow keys to move between its controls, with the whole
 * group being a single tab stop. Honouring it means roving tabindex over
 * children this component does not own and cannot see the shape of, including
 * comboboxes and date fields that want the arrow keys for themselves.
 *
 * Declaring the role without implementing the movement is worse than not
 * declaring it. It tells somebody a structure exists, they press Right, nothing
 * happens, and they are left unsure whether the page is broken or they are. So
 * this is a labelled group of ordinary tab stops, which is what it actually is.
 *
 * ## Sticky, because a filter you cannot see is a filter you forget
 *
 * Scrolled two hundred rows into a filtered list, the most common mistake is
 * reading the visible rows as the whole set. The bar stays, and it is where the
 * count of what is being shown belongs.
 */

import type { ReactNode } from 'react'
import styles from './Toolbar.module.css'

export interface ToolbarProps {
  /**
   * Names the group: "Case filters". Required, because a `<div>` full of
   * controls with no name is announced as nothing at all, and this one usually
   * sits between two other groups of controls.
   */
  label: string
  /** Filters and search, on the left. */
  children?: ReactNode
  /** Buttons, on the right. Primary action last. */
  actions?: ReactNode
  /**
   * What is being shown, in words: "412 cases, 12 selected". Rendered in a
   * polite live region, so a filter that cuts a list from 412 rows to 3 says so
   * to somebody who cannot see the table shrink.
   */
  summary?: ReactNode
  /** Turns off the sticky positioning, for a toolbar not above a scrolling list. */
  stuck?: boolean
  className?: string
}

export function Toolbar({
  label,
  children,
  actions,
  summary,
  stuck = true,
  className,
}: ToolbarProps) {
  const toolbarClassName = [styles.toolbar, stuck ? styles.sticky : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={toolbarClassName} aria-label={label} role="group">
      <div className={styles.filters}>{children}</div>

      {summary ? (
        <p className={styles.summary} role="status" aria-live="polite">
          {summary}
        </p>
      ) : null}

      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  )
}
